import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Flex, Input, Spinner, Text, VStack } from '@chakra-ui/react'
import {
  LuLock,
  LuPencil,
  LuPlus,
  LuTrash2,
  LuEye,
  LuEyeOff,
  LuCopy,
  LuChevronDown,
  LuSearch,
  LuShield,
  LuFolder,
  LuLink,
  LuInfo,
  LuCheck,
} from 'react-icons/lu'
import { PageHeader, Modal, ConfirmDialog, toaster } from '@minetrace/ui'
import { useTranslation } from 'react-i18next'
import { themeColors, brand } from '../../../theme'
import { menusService, type MenuItemAdminResponse } from '../../../api/services/menus.service'
import { permissionsService, type PermissionResponse } from '../../../api/services/permissions.service'
import { useHasPermission } from '../../../auth/PermissionContext'
import { MenuIcon, iconNames } from '../../../auth/iconMap'
import { MenuItemFieldLengths } from '../../../lib/fieldLengths'
import { resolveMenuLabel } from '../../../auth/resolveMenuLabel'
import { useAuth } from '../../../contexts/AuthContext'

interface EditState {
  mode: 'create' | 'edit'
  item: MenuItemAdminResponse | null
  parentId: string | null
  code: string
  label: string
  route: string
  icon: string
  order: number
  isGroup: boolean
  isHidden: boolean
  isComingSoon: boolean
  requiredPermissionCode: string
}

const EMPTY_EDIT: EditState = {
  mode: 'create',
  item: null,
  parentId: null,
  code: '',
  label: '',
  route: '',
  icon: '',
  order: 0,
  isGroup: false,
  isHidden: false,
  isComingSoon: false,
  requiredPermissionCode: '',
}

export default function MenusAdminPage() {
  const colors = themeColors
  const allowed = useHasPermission('menus.manage')
  const { user } = useAuth()
  const isPlatform = user?.isPlatform ?? false
  const { t } = useTranslation()

  const [items, setItems] = useState<MenuItemAdminResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const [editing, setEditing] = useState<EditState | null>(null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState<MenuItemAdminResponse | null>(null)

  // Lazy-loaded once and shared across edits — small (~55 rows) and
  // unchanging during a session, so a single fetch is plenty.
  const [perms, setPerms] = useState<PermissionResponse[]>([])
  useEffect(() => {
    if (!allowed) return
    permissionsService.list().then(setPerms).catch(() => { /* non-blocking */ })
  }, [allowed])

  const refresh = () => {
    if (!allowed) return
    let cancelled = false
    setLoading(true)
    menusService
      .list()
      .then(list => { if (!cancelled) setItems(list) })
      .catch(() => { if (!cancelled) setError('No se pudo cargar el catálogo de menús') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }

  useEffect(refresh, [allowed])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter(m =>
      m.code.toLowerCase().includes(q) ||
      m.label.toLowerCase().includes(q) ||
      (m.route ?? '').toLowerCase().includes(q),
    )
  }, [items, search])

  // Build a tree by parentId for rendering. Roots are rows with parentId == null.
  const childrenByParent = useMemo(() => {
    const map = new Map<string | null, MenuItemAdminResponse[]>()
    for (const it of filtered) {
      const key = it.parentId
      const list = map.get(key) ?? []
      list.push(it)
      map.set(key, list)
    }
    for (const list of map.values()) list.sort((a, b) => a.order - b.order)
    return map
  }, [filtered])

  const openCreate = (parentId: string | null) => {
    setEditing({ ...EMPTY_EDIT, mode: 'create', parentId })
  }

  const openEdit = (item: MenuItemAdminResponse) => {
    setEditing({
      mode: 'edit',
      item,
      parentId: item.parentId,
      code: item.code,
      label: item.label,
      route: item.route ?? '',
      icon: item.icon ?? '',
      order: item.order,
      isGroup: item.isGroup,
      isHidden: item.isHidden,
      isComingSoon: item.isComingSoon,
      requiredPermissionCode: item.requiredPermissionCode ?? '',
    })
  }

  const handleSave = async () => {
    if (!editing) return
    setSaving(true)
    try {
      if (editing.mode === 'create') {
        await menusService.create({
          parentId: editing.parentId,
          code: editing.code.trim(),
          label: editing.label.trim(),
          route: editing.route.trim() || null,
          icon: editing.icon.trim() || null,
          order: Number(editing.order) || 0,
          isGroup: editing.isGroup,
          isComingSoon: editing.isComingSoon,
          requiredPermissionCode: editing.requiredPermissionCode.trim() || null,
          tenantId: null,
        })
        toaster.success({ description: 'Menú creado' })
      } else if (editing.item) {
        await menusService.update(editing.item.id, {
          parentId: editing.parentId,
          label: editing.label.trim(),
          route: editing.route.trim() || null,
          icon: editing.icon.trim() || null,
          order: Number(editing.order) || 0,
          isGroup: editing.isGroup,
          isHidden: editing.isHidden,
          isComingSoon: editing.isComingSoon,
          requiredPermissionCode: editing.requiredPermissionCode.trim() || null,
        })
        toaster.success({ description: 'Menú actualizado' })
      }
      setEditing(null)
      refresh()
    } catch {
      // Toast handled by api client
    } finally {
      setSaving(false)
    }
  }

  const handleOverride = async (item: MenuItemAdminResponse) => {
    try {
      await menusService.override(item.id)
      toaster.success({ description: 'Overlay creado. Edítelo para personalizar.' })
      refresh()
    } catch {
      // Toast handled
    }
  }

  const handleDelete = async () => {
    if (!deleting) return
    try {
      await menusService.delete(deleting.id)
      toaster.success({ description: 'Menú eliminado' })
      setDeleting(null)
      refresh()
    } catch {
      setDeleting(null)
    }
  }

  const handleToggleHidden = async (item: MenuItemAdminResponse) => {
    try {
      await menusService.update(item.id, {
        parentId: item.parentId,
        label: item.label,
        route: item.route,
        icon: item.icon,
        order: item.order,
        isGroup: item.isGroup,
        isHidden: !item.isHidden,
        isComingSoon: item.isComingSoon,
        requiredPermissionCode: item.requiredPermissionCode,
      })
      toaster.success({ description: item.isHidden ? 'Menú visible' : 'Menú oculto' })
      refresh()
    } catch {
      // Toast handled
    }
  }

  const canEditRow = (item: MenuItemAdminResponse): boolean => {
    if (item.tenantId == null) return isPlatform
    return true
  }

  const renderRow = (item: MenuItemAdminResponse, depth: number) => {
    const kids = childrenByParent.get(item.id) ?? []
    const editable = canEditRow(item)
    const isTenantOverlay = item.isOverlay
    const scopeBadge = item.tenantId == null
      ? { label: 'Global', bg: '#0EA5E920', color: '#0EA5E9' }
      : isTenantOverlay
      ? { label: 'Overlay', bg: '#A855F720', color: '#A855F7' }
      : { label: 'Tenant', bg: '#2a8a8720', color: '#2a8a87' }

    return (
      <Box key={item.id}>
        <Flex
          alignItems="center"
          gap={3}
          px={4}
          py={2.5}
          pl={4 + depth * 5}
          borderTop={depth === 0 ? 'none' : '1px solid'}
          borderColor={colors.border}
          opacity={item.isHidden ? 0.55 : 1}
          _hover={{ bg: colors.bgHover }}
        >
          {item.icon && <MenuIcon name={item.icon} size={16} color={colors.textMuted} />}
          <Box flex="0 0 280px">
            <Text fontSize="xs" fontFamily="mono" color={colors.textDimmed}>{item.code}</Text>
            <Text fontSize="sm" fontWeight="500" color={colors.text}>{item.label}</Text>
          </Box>
          <Box flex="0 0 220px">
            <Text fontSize="xs" color={colors.textMuted} fontFamily="mono">{item.route ?? '—'}</Text>
            {item.requiredPermissionCode && (
              <Text fontSize="2xs" color={colors.textDimmed}>perm: {item.requiredPermissionCode}</Text>
            )}
          </Box>
          <Box display="inline-block" px={2} py={0.5} borderRadius="md" fontSize="10px" fontWeight="700" bg={scopeBadge.bg} color={scopeBadge.color}>
            {scopeBadge.label}
          </Box>
          {item.isGroup && (
            <Text fontSize="2xs" color={colors.textDimmed} px={2} py={0.5} borderRadius="md" bg={colors.bgCard}>
              GRUPO
            </Text>
          )}
          {item.isHidden && (
            <Text fontSize="2xs" color={colors.textDimmed} px={2} py={0.5} borderRadius="md" bg={colors.bgCard}>
              OCULTO
            </Text>
          )}
          <Box flex="1" />
          <Flex gap={1} alignItems="center">
            {editable && (
              <Box as="button" p={1.5} borderRadius="md" cursor="pointer" color={colors.textMuted} _hover={{ bg: colors.bgCard, color: brand.green }} onClick={() => openEdit(item)} title="Editar">
                <LuPencil size={14} />
              </Box>
            )}
            {item.tenantId != null && (
              <Box as="button" p={1.5} borderRadius="md" cursor="pointer" color={colors.textMuted} _hover={{ bg: colors.bgCard, color: brand.green }} onClick={() => handleToggleHidden(item)} title={item.isHidden ? 'Mostrar' : 'Ocultar'}>
                {item.isHidden ? <LuEye size={14} /> : <LuEyeOff size={14} />}
              </Box>
            )}
            {item.tenantId == null && !item.hasOverlay && !isPlatform && (
              <Box as="button" p={1.5} borderRadius="md" cursor="pointer" color={colors.textMuted} _hover={{ bg: colors.bgCard, color: brand.green }} onClick={() => handleOverride(item)} title="Crear overlay para personalizar">
                <LuCopy size={14} />
              </Box>
            )}
            {editable && !item.isSystem && (
              <Box as="button" p={1.5} borderRadius="md" cursor="pointer" color={colors.textMuted} _hover={{ bg: colors.bgCard, color: '#c8442f' }} onClick={() => setDeleting(item)} title="Eliminar">
                <LuTrash2 size={14} />
              </Box>
            )}
            {item.isGroup && editable && (
              <Box as="button" p={1.5} borderRadius="md" cursor="pointer" color={colors.textMuted} _hover={{ bg: colors.bgCard, color: brand.green }} onClick={() => openCreate(item.id)} title="Agregar hijo">
                <LuPlus size={14} />
              </Box>
            )}
          </Flex>
        </Flex>
        {kids.map(child => renderRow(child, depth + 1))}
      </Box>
    )
  }

  if (!allowed) {
    return (
      <Box p={8}>
        <Flex alignItems="center" gap={3} color={colors.textMuted}>
          <LuLock size={18} />
          <Text>No tiene permiso para gestionar menús.</Text>
        </Flex>
      </Box>
    )
  }

  const roots = childrenByParent.get(null) ?? []

  return (
    <Box>
      <PageHeader
        title="Editor de menús"
        subtitle={
          isPlatform
            ? 'Edita menús globales del sistema y los overlays/menús personalizados de cada tenant.'
            : 'Personaliza los menús globales con overlays o agrega menús propios de tu organización.'
        }
        colors={colors}
      />

      <Flex gap={2} mb={4} alignItems="center" flexWrap="wrap">
        <Input
          placeholder="Filtrar por código, etiqueta o ruta…"
          value={search}
          onChange={e => setSearch(e.target.value.slice(0, 200))}
          maxLength={200}
          maxW="360px"
        />
        <Box flex="1" />
        <Box
          as="button"
          display="inline-flex"
          alignItems="center"
          gap={1.5}
          px={4}
          py={2}
          borderRadius="md"
          bg={brand.green}
          color="white"
          cursor="pointer"
          fontSize="sm"
          fontWeight="600"
          _hover={{ opacity: 0.9 }}
          onClick={() => openCreate(null)}
        >
          <LuPlus size={14} />
          <Text>Nuevo menú raíz</Text>
        </Box>
      </Flex>

      {loading && (
        <Flex justifyContent="center" py={10}><Spinner color={brand.green} /></Flex>
      )}

      {error && !loading && (
        <Text color="red.500">{error}</Text>
      )}

      {!loading && !error && (
        <Box border="1px solid" borderColor={colors.border} borderRadius="xl" bg={colors.bgPanel} overflow="hidden">
          {roots.length === 0 ? (
            <Text p={6} color={colors.textMuted}>Sin menús que coincidan con la búsqueda.</Text>
          ) : (
            roots.map(r => renderRow(r, 0))
          )}
        </Box>
      )}

      <Modal
        open={editing !== null}
        onClose={() => !saving && setEditing(null)}
        title={editing?.mode === 'create' ? 'Nuevo menú' : 'Editar menú'}
        size="md"
        footer={
          <>
            <Box as="button" type="button" px={5} py={2.5} borderRadius="md" fontSize="sm" fontWeight="600" color={colors.textSecondary} bg={colors.bgHover} cursor="pointer" _hover={{ bg: colors.bgCard }} onClick={() => setEditing(null)}>
              Cancelar
            </Box>
            <Box as="button" type="button" px={5} py={2.5} borderRadius="md" fontSize="sm" fontWeight="600" color="white" bg={brand.green} cursor={saving ? 'not-allowed' : 'pointer'} opacity={saving ? 0.7 : 1} _hover={{ opacity: 0.9 }} onClick={handleSave} aria-disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </Box>
          </>
        }
      >
        {editing && (
          <MenuEditForm
            editing={editing}
            setEditing={setEditing}
            perms={perms}
            t={t}
            colors={colors}
          />
        )}
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Eliminar menú"
        variant="danger"
        confirmLabel="Eliminar"
      >
        ¿Eliminar “{deleting?.label}”? Si tiene hijos, primero debe eliminarlos. Esta acción no se puede deshacer.
      </ConfirmDialog>
    </Box>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// MenuEditForm — the redesigned modal body. Replaces the raw text-only form
// with smarter inputs: a label section that handles `i18n:<code>` markers
// without exposing them, a visual icon picker, and a permission combobox.
// ─────────────────────────────────────────────────────────────────────────────

interface MenuEditFormProps {
  editing: EditState
  setEditing: (next: EditState) => void
  perms: PermissionResponse[]
  t: ReturnType<typeof useTranslation>['t']
  colors: typeof themeColors
}

const I18N_MARKER = 'i18n:'

function MenuEditForm({ editing, setEditing, perms, t, colors }: MenuEditFormProps) {
  // System items use `i18n:<code>` markers in the label. Track whether the
  // admin is editing the marker (default: keep using translations) or
  // overriding with a literal label.
  const startsAsMarker = editing.label.startsWith(I18N_MARKER)
  const [overrideLabel, setOverrideLabel] = useState(!startsAsMarker)

  const isSystem = editing.item?.isSystem ?? false
  const labelPreview = resolveMenuLabel(editing.label || `i18n:${editing.code}`, t)

  return (
    <VStack gap={4} alignItems="stretch">
      {/* Live preview pill so admins see exactly what their edit will look
          like in the sidebar/dropdown before saving. */}
      <Box bg={colors.bgCard} borderRadius="lg" p={3}>
        <Text fontSize="2xs" fontWeight="700" color={colors.textDimmed} textTransform="uppercase" letterSpacing="0.05em" mb={2}>
          Vista previa
        </Text>
        <Flex alignItems="center" gap={2}>
          <MenuIcon name={editing.icon || 'LuLayoutDashboard'} size={18} color={colors.text} />
          <Text fontSize="sm" fontWeight="500" color={colors.text}>
            {labelPreview || <Text as="span" color={colors.textDimmed}>(sin etiqueta)</Text>}
          </Text>
          {editing.isComingSoon && (
            <Text fontSize="2xs" color={colors.textDimmed} bg={colors.bgPanel} px={1.5} py={0.5} borderRadius="full" ml="auto">
              Pronto
            </Text>
          )}
        </Flex>
      </Box>

      {/* Code (only shown on create — system codes are immutable on edit). */}
      {editing.mode === 'create' && (
        <Box>
          <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>
            Código <Text as="span" color="red.500">*</Text>
          </Text>
          <Input
            value={editing.code}
            onChange={e => setEditing({ ...editing, code: e.target.value.slice(0, MenuItemFieldLengths.code) })}
            placeholder="ej. masters.configuration.tarifas"
            fontFamily="mono"
            fontSize="sm"
            maxLength={MenuItemFieldLengths.code}
          />
          <Flex justifyContent="space-between" alignItems="center" mt={1}>
            <Text fontSize="2xs" color={colors.textDimmed}>
              Identificador único usado por el código. Mantén el formato dotted (ej. <Text as="span" fontFamily="mono">module.section.item</Text>).
            </Text>
            <Text fontSize="2xs" color={colors.textDimmed} fontVariantNumeric="tabular-nums">
              {editing.code.length}/{MenuItemFieldLengths.code}
            </Text>
          </Flex>
        </Box>
      )}

      {/* Label section — different UX depending on whether it's a system item
          (uses i18n marker) or a custom item (literal text). */}
      <Box>
        <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>
          Etiqueta visible <Text as="span" color="red.500">*</Text>
        </Text>

        {isSystem && !overrideLabel ? (
          <>
            <Flex
              alignItems="center"
              gap={2}
              px={3}
              py={2.5}
              borderRadius="md"
              bg={colors.bgCard}
              border="1px solid"
              borderColor={colors.border}
            >
              <LuInfo size={14} color={colors.textMuted} />
              <Text fontSize="sm" color={colors.text} flex="1">
                {labelPreview}
              </Text>
              <Text fontSize="2xs" color={colors.textDimmed} fontFamily="mono">
                traducido
              </Text>
            </Flex>
            <Flex justifyContent="space-between" alignItems="center" mt={1}>
              <Text fontSize="2xs" color={colors.textDimmed}>
                Item del sistema. La etiqueta se traduce automáticamente según el idioma del usuario.
              </Text>
              <Box
                as="button"
                fontSize="2xs"
                color={brand.green}
                cursor="pointer"
                _hover={{ textDecoration: 'underline' }}
                onClick={() => {
                  setOverrideLabel(true)
                  setEditing({ ...editing, label: labelPreview })
                }}
              >
                Reemplazar con texto literal
              </Box>
            </Flex>
          </>
        ) : (
          <>
            <Input
              value={editing.label}
              onChange={e => setEditing({ ...editing, label: e.target.value.slice(0, MenuItemFieldLengths.label) })}
              placeholder="ej. Tarifas"
              maxLength={MenuItemFieldLengths.label}
            />
            <Text fontSize="2xs" color={colors.textDimmed} mt={1} textAlign="right" fontVariantNumeric="tabular-nums">
              {editing.label.length}/{MenuItemFieldLengths.label}
            </Text>
            {isSystem && (
              <Flex justifyContent="space-between" alignItems="center" mt={1}>
                <Text fontSize="2xs" color={colors.textDimmed}>
                  Texto literal — no se traduce. Solo aplica a este tenant.
                </Text>
                <Box
                  as="button"
                  fontSize="2xs"
                  color={brand.green}
                  cursor="pointer"
                  _hover={{ textDecoration: 'underline' }}
                  onClick={() => {
                    setOverrideLabel(false)
                    setEditing({ ...editing, label: `i18n:${editing.code}` })
                  }}
                >
                  Volver a traducción automática
                </Box>
              </Flex>
            )}
          </>
        )}
      </Box>

      {/* Item type — explicit radio so admins know what "is group" means. */}
      <Box>
        <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1.5}>
          Tipo
        </Text>
        <Flex gap={2}>
          <TypeOption
            active={!editing.isGroup}
            icon={<LuLink size={14} />}
            title="Enlace navegable"
            subtitle="Lleva al usuario a una ruta cuando hace clic"
            onClick={() => setEditing({ ...editing, isGroup: false })}
            colors={colors}
          />
          <TypeOption
            active={editing.isGroup}
            icon={<LuFolder size={14} />}
            title="Grupo (encabezado)"
            subtitle="Agrupa items hijos en un dropdown — sin ruta propia"
            onClick={() => setEditing({ ...editing, isGroup: true, route: '' })}
            colors={colors}
          />
        </Flex>
      </Box>

      {/* Route + Order — Route hidden for groups since it's meaningless. */}
      <Flex gap={3}>
        {!editing.isGroup && (
          <Box flex={1}>
            <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>Ruta</Text>
            <Input
              value={editing.route}
              onChange={e => setEditing({ ...editing, route: e.target.value.slice(0, MenuItemFieldLengths.route) })}
              placeholder="/app/..."
              fontFamily="mono"
              fontSize="sm"
              maxLength={MenuItemFieldLengths.route}
            />
            <Text fontSize="2xs" color={colors.textDimmed} mt={1} textAlign="right" fontVariantNumeric="tabular-nums">
              {editing.route.length}/{MenuItemFieldLengths.route}
            </Text>
          </Box>
        )}
        <Box flex="0 0 90px">
          <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>Orden</Text>
          <Input
            type="number"
            value={editing.order}
            onChange={e => setEditing({ ...editing, order: Number(e.target.value) })}
          />
        </Box>
      </Flex>

      {/* Visual icon picker. */}
      <Box>
        <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>Ícono</Text>
        <IconPicker
          value={editing.icon}
          onChange={icon => setEditing({ ...editing, icon })}
          colors={colors}
        />
      </Box>

      {/* Permission combobox grouped by module. */}
      <Box>
        <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>Permiso requerido</Text>
        <PermissionPicker
          value={editing.requiredPermissionCode}
          onChange={code => setEditing({ ...editing, requiredPermissionCode: code })}
          perms={perms}
          colors={colors}
        />
        <Text fontSize="2xs" color={colors.textDimmed} mt={1}>
          Solo los usuarios con este permiso verán el item habilitado en el menú.
        </Text>
      </Box>

      {/* State flags. */}
      <Box>
        <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1.5}>Estado</Text>
        <Flex gap={5} alignItems="center" flexWrap="wrap">
          <Flex as="label" alignItems="center" gap={2} cursor="pointer">
            <input
              type="checkbox"
              checked={editing.isComingSoon}
              onChange={e => setEditing({ ...editing, isComingSoon: e.target.checked })}
              style={{ accentColor: brand.green }}
            />
            <Text fontSize="sm" color={colors.text}>Próximamente</Text>
          </Flex>
          {editing.mode === 'edit' && (
            <Flex as="label" alignItems="center" gap={2} cursor="pointer">
              <input
                type="checkbox"
                checked={editing.isHidden}
                onChange={e => setEditing({ ...editing, isHidden: e.target.checked })}
                style={{ accentColor: brand.green }}
              />
              <Text fontSize="sm" color={colors.text}>Oculto</Text>
            </Flex>
          )}
        </Flex>
      </Box>

      {/* Compact metadata strip — replaces the old monospace dump. */}
      {editing.mode === 'edit' && editing.item && (
        <Flex gap={2} flexWrap="wrap" pt={2} borderTop="1px solid" borderColor={colors.border}>
          <MetaChip label="Code" value={editing.item.code} mono colors={colors} />
          <MetaChip
            label="Scope"
            value={editing.item.tenantId == null ? 'Global' : editing.item.isOverlay ? 'Overlay' : 'Tenant'}
            colors={colors}
          />
          {editing.item.isSystem && (
            <Flex alignItems="center" gap={1} fontSize="2xs" color={colors.textMuted} bg={colors.bgCard} px={2} py={1} borderRadius="md">
              <LuShield size={11} />
              <Text>Sistema</Text>
            </Flex>
          )}
        </Flex>
      )}
    </VStack>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────────

interface TypeOptionProps {
  active: boolean
  icon: React.ReactNode
  title: string
  subtitle: string
  onClick: () => void
  colors: typeof themeColors
}

function TypeOption({ active, icon, title, subtitle, onClick, colors }: TypeOptionProps) {
  return (
    <Box
      as="button"
      flex="1"
      textAlign="left"
      px={3}
      py={2.5}
      borderRadius="md"
      border="2px solid"
      borderColor={active ? brand.green : colors.border}
      bg={active ? 'rgba(42, 138, 135, 0.06)' : 'white'}
      cursor="pointer"
      onClick={onClick}
      _hover={{ borderColor: active ? brand.green : colors.textMuted }}
      transition="all 0.15s"
    >
      <Flex alignItems="center" gap={1.5} mb={0.5} color={active ? brand.green : colors.text}>
        {icon}
        <Text fontSize="xs" fontWeight="600">{title}</Text>
        {active && <LuCheck size={12} style={{ marginLeft: 'auto' }} />}
      </Flex>
      <Text fontSize="2xs" color={colors.textDimmed} lineHeight="1.3">{subtitle}</Text>
    </Box>
  )
}

interface MetaChipProps {
  label: string
  value: string
  mono?: boolean
  colors: typeof themeColors
}

function MetaChip({ label, value, mono, colors }: MetaChipProps) {
  return (
    <Flex
      alignItems="center"
      gap={1}
      fontSize="2xs"
      bg={colors.bgCard}
      px={2}
      py={1}
      borderRadius="md"
    >
      <Text color={colors.textDimmed} fontWeight="600">{label}:</Text>
      <Text color={colors.text} fontFamily={mono ? 'mono' : undefined}>{value}</Text>
    </Flex>
  )
}

interface IconPickerProps {
  value: string
  onChange: (icon: string) => void
  colors: typeof themeColors
}

function IconPicker({ value, onChange, colors }: IconPickerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Close on outside click — typical popover behavior.
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return iconNames
    return iconNames.filter(n => n.toLowerCase().includes(q))
  }, [query])

  return (
    <Box position="relative" ref={containerRef}>
      <Flex
        as="button"
        alignItems="center"
        gap={2}
        w="100%"
        px={3}
        py={2}
        borderRadius="md"
        border="1px solid"
        borderColor={colors.border}
        bg="white"
        cursor="pointer"
        onClick={() => setOpen(o => !o)}
        _hover={{ borderColor: colors.textMuted }}
      >
        <MenuIcon name={value || 'LuLayoutDashboard'} size={16} color={colors.text} />
        <Text fontSize="sm" color={value ? colors.text : colors.textDimmed} fontFamily="mono" flex="1" textAlign="left">
          {value || '(sin ícono)'}
        </Text>
        <LuChevronDown size={14} color={colors.textMuted} style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.15s' }} />
      </Flex>

      {open && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          right={0}
          bg="white"
          border="1px solid"
          borderColor={colors.border}
          borderRadius="md"
          boxShadow="lg"
          zIndex={1000}
          p={2}
          maxH="300px"
          overflowY="auto"
        >
          <Flex
            alignItems="center"
            gap={1.5}
            px={2}
            py={1.5}
            mb={2}
            borderRadius="md"
            bg={colors.bgCard}
          >
            <LuSearch size={14} color={colors.textMuted} />
            <Input
              variant="outline"
              border="none"
              p={0}
              h="auto"
              fontSize="sm"
              placeholder="Buscar ícono…"
              value={query}
              onChange={e => setQuery(e.target.value.slice(0, 60))}
              maxLength={60}
              _focus={{ outline: 'none', boxShadow: 'none' }}
            />
            {value && (
              <Box
                as="button"
                fontSize="2xs"
                color={colors.textMuted}
                cursor="pointer"
                onClick={() => { onChange(''); setOpen(false) }}
                _hover={{ color: brand.green }}
              >
                Quitar
              </Box>
            )}
          </Flex>
          <Box
            display="grid"
            gridTemplateColumns="repeat(8, 1fr)"
            gap={1}
          >
            {filtered.map(name => {
              const selected = name === value
              return (
                <Box
                  key={name}
                  as="button"
                  p={2}
                  borderRadius="md"
                  cursor="pointer"
                  bg={selected ? 'rgba(42, 138, 135, 0.12)' : 'transparent'}
                  color={selected ? brand.green : colors.text}
                  border="1px solid"
                  borderColor={selected ? brand.green : 'transparent'}
                  _hover={{ bg: colors.bgCard }}
                  onClick={() => { onChange(name); setOpen(false) }}
                  title={name}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <MenuIcon name={name} size={16} />
                </Box>
              )
            })}
            {filtered.length === 0 && (
              <Text gridColumn="1 / -1" textAlign="center" py={4} color={colors.textDimmed} fontSize="xs">
                Sin resultados
              </Text>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}

interface PermissionPickerProps {
  value: string
  onChange: (code: string) => void
  perms: PermissionResponse[]
  colors: typeof themeColors
}

function PermissionPicker({ value, onChange, perms, colors }: PermissionPickerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Group by module so the dropdown reads as a structured catalog instead
  // of a flat 50+ row blob.
  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase()
    const filtered = q
      ? perms.filter(p =>
          p.code.toLowerCase().includes(q) ||
          p.displayName.toLowerCase().includes(q) ||
          p.module.toLowerCase().includes(q))
      : perms
    const map = new Map<string, PermissionResponse[]>()
    for (const p of filtered) {
      const list = map.get(p.module) ?? []
      list.push(p)
      map.set(p.module, list)
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [perms, query])

  const selected = perms.find(p => p.code === value)

  return (
    <Box position="relative" ref={containerRef}>
      <Flex
        as="button"
        alignItems="center"
        gap={2}
        w="100%"
        px={3}
        py={2}
        borderRadius="md"
        border="1px solid"
        borderColor={colors.border}
        bg="white"
        cursor="pointer"
        onClick={() => setOpen(o => !o)}
        _hover={{ borderColor: colors.textMuted }}
      >
        {selected ? (
          <Flex flex="1" alignItems="center" gap={2} textAlign="left">
            <Text fontSize="sm" color={colors.text}>{selected.displayName}</Text>
            <Text fontSize="2xs" color={colors.textDimmed} fontFamily="mono">{selected.code}</Text>
          </Flex>
        ) : (
          <Text flex="1" fontSize="sm" color={colors.textDimmed} textAlign="left">
            Sin restricción (visible para todos)
          </Text>
        )}
        <LuChevronDown size={14} color={colors.textMuted} style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.15s' }} />
      </Flex>

      {open && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          right={0}
          bg="white"
          border="1px solid"
          borderColor={colors.border}
          borderRadius="md"
          boxShadow="lg"
          zIndex={1000}
          maxH="320px"
          overflowY="auto"
        >
          <Flex
            position="sticky"
            top={0}
            bg="white"
            alignItems="center"
            gap={1.5}
            px={2}
            py={2}
            borderBottom="1px solid"
            borderColor={colors.border}
            zIndex={1}
          >
            <Box flex="1" position="relative">
              <Box position="absolute" left={2} top="50%" transform="translateY(-50%)" pointerEvents="none">
                <LuSearch size={14} color={colors.textMuted} />
              </Box>
              <Input
                pl={8}
                size="sm"
                placeholder="Buscar permiso…"
                value={query}
                onChange={e => setQuery(e.target.value.slice(0, 80))}
                maxLength={80}
              />
            </Box>
          </Flex>
          <Box
            as="button"
            display="block"
            w="100%"
            textAlign="left"
            px={3}
            py={2}
            cursor="pointer"
            bg={!value ? 'rgba(42, 138, 135, 0.06)' : 'transparent'}
            color={!value ? brand.green : colors.text}
            _hover={{ bg: colors.bgCard }}
            onClick={() => { onChange(''); setOpen(false) }}
            borderBottom="1px solid"
            borderColor={colors.border}
          >
            <Flex alignItems="center" gap={2}>
              <Text fontSize="sm" fontWeight="500">Sin restricción</Text>
              {!value && <LuCheck size={14} style={{ marginLeft: 'auto' }} />}
            </Flex>
            <Text fontSize="2xs" color={colors.textDimmed}>Todos los usuarios autenticados verán este menú</Text>
          </Box>

          {grouped.map(([module, list]) => (
            <Box key={module}>
              <Text
                fontSize="2xs"
                fontWeight="700"
                color={colors.textDimmed}
                textTransform="uppercase"
                letterSpacing="0.05em"
                px={3}
                py={1.5}
                bg={colors.bgCard}
              >
                {module}
              </Text>
              {list.map(p => {
                const isSel = p.code === value
                return (
                  <Box
                    key={p.id}
                    as="button"
                    display="block"
                    w="100%"
                    textAlign="left"
                    px={3}
                    py={2}
                    cursor="pointer"
                    bg={isSel ? 'rgba(42, 138, 135, 0.06)' : 'transparent'}
                    _hover={{ bg: colors.bgCard }}
                    onClick={() => { onChange(p.code); setOpen(false) }}
                  >
                    <Flex alignItems="center" gap={2}>
                      <Text fontSize="sm" fontWeight={isSel ? '600' : '500'} color={isSel ? brand.green : colors.text}>
                        {p.displayName}
                      </Text>
                      <Text fontSize="2xs" color={colors.textDimmed} fontFamily="mono" ml="auto">
                        {p.code}
                      </Text>
                      {isSel && <LuCheck size={14} color={brand.green} />}
                    </Flex>
                    {p.description && (
                      <Text fontSize="2xs" color={colors.textDimmed} mt={0.5}>{p.description}</Text>
                    )}
                  </Box>
                )
              })}
            </Box>
          ))}

          {grouped.length === 0 && (
            <Text textAlign="center" py={4} color={colors.textDimmed} fontSize="xs">
              Sin resultados
            </Text>
          )}
        </Box>
      )}
    </Box>
  )
}
